/* ForgeHeavy — "Frequently forged together".
   Two jobs, neither of which touches Dawn's cart or variant code:
     1. Fetch this section back through Shopify's Product Recommendations API
        (intent=complementary, falling back to intent=related) and inject the result.
     2. Run the checkbox / live total / "Add Selected" behaviour.

   Adding to cart uses the standard Cart AJAX API (/cart/add.js with an `items` array),
   then hands off to Dawn's own <cart-drawer> to render and open. */
if (!customElements.get('fh-forged')) {
  class FhForged extends HTMLElement {
    connectedCallback() {
      if (this.dataset.loaded) return;

      // Already server-rendered (the recommendations endpoint response) — just wire it up.
      if (this.querySelector('[data-fh-forged-add]')) {
        this.bind();
        return;
      }

      // Defer the fetch until the section is nearly in view.
      this.observer = new IntersectionObserver(
        (entries, observer) => {
          if (!entries[0].isIntersecting) return;
          observer.unobserve(this);
          this.load();
        },
        { rootMargin: '0px 0px 400px 0px' }
      );
      this.observer.observe(this);
    }

    load() {
      this.dataset.loaded = 'true';
      // Complementary needs the Search & Discovery app; fall back to related.
      this.fetchIntent('complementary').then((filled) => {
        if (!filled) this.fetchIntent('related');
      });
    }

    fetchIntent(intent) {
      const url =
        this.dataset.url +
        '&intent=' + intent +
        '&product_id=' + this.dataset.productId +
        '&section_id=' + this.dataset.sectionId;

      return fetch(url)
        .then((r) => r.text())
        .then((text) => {
          const holder = document.createElement('div');
          holder.innerHTML = text;
          const source = holder.querySelector('fh-forged');
          // Empty means the API returned too few addable products — leave the section
          // hidden rather than rendering an empty band.
          if (!source || !source.innerHTML.trim().length) return false;
          this.innerHTML = source.innerHTML;
          this.bind();
          return true;
        })
        .catch((e) => {
          console.error(e);
          return false;
        });
    }

    bind() {
      this.inputs = Array.from(this.querySelectorAll('.fh-forged__input'));
      this.totalEl = this.querySelector('[data-fh-forged-total]');
      this.button = this.querySelector('[data-fh-forged-add]');
      this.errorEl = this.querySelector('[data-fh-forged-error]');
      if (!this.inputs.length || !this.button) return;

      this.inputs.forEach((input) => {
        input.addEventListener('change', () => {
          input.closest('.fh-forged__card')?.classList.toggle('is-checked', input.checked);
          this.renderTotal();
        });
      });

      this.button.addEventListener('click', this.addSelected.bind(this));
      this.renderTotal();
    }

    checked() {
      return this.inputs.filter((i) => i.checked);
    }

    renderTotal() {
      const cents = this.checked().reduce((sum, i) => sum + parseInt(i.dataset.price, 10), 0);
      if (this.totalEl) this.totalEl.textContent = this.formatMoney(cents);
      this.button.disabled = this.checked().length === 0;
    }

    formatMoney(cents) {
      const currency = this.dataset.currency || 'USD';
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en', {
          style: 'currency',
          currency: currency,
        }).format(cents / 100);
      } catch (e) {
        return (cents / 100).toFixed(2);
      }
    }

    addSelected() {
      const items = this.checked().map((i) => ({ id: parseInt(i.dataset.variantId, 10), quantity: 1 }));
      if (!items.length) return;

      const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      const label = this.querySelector('[data-fh-forged-add-label]');
      const original = label ? label.textContent : '';

      this.button.disabled = true;
      if (label) label.textContent = '…';
      if (this.errorEl) this.errorEl.hidden = true;

      const body = { items: items };
      // Ask for the drawer's sections back so Dawn can repaint it in one pass.
      if (cart && typeof cart.getSectionsToRender === 'function') {
        body.sections = cart.getSectionsToRender().map((section) => section.id);
        body.sections_url = window.location.pathname;
      }

      fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
        body: JSON.stringify(body),
      })
        .then((r) => r.json())
        .then((response) => {
          if (response.status) throw new Error(response.description || response.message);
          // Hand off to Dawn's drawer — it renders the new contents and opens itself.
          if (cart && typeof cart.renderContents === 'function') {
            cart.renderContents(response);
          } else {
            window.location.href = window.Shopify.routes.root + 'cart';
          }
        })
        .catch((e) => {
          console.error(e);
          if (this.errorEl) {
            this.errorEl.textContent = e.message || 'Could not add these to your cart.';
            this.errorEl.hidden = false;
          }
        })
        .finally(() => {
          if (label) label.textContent = original;
          this.renderTotal();
        });
    }
  }

  customElements.define('fh-forged', FhForged);
}
